import {createPopper, Instance} from "@popperjs/core";
import {Partial} from "split.js";
import TomSelect from "tom-select";
import {TPluginHash, TPluginItem} from "tom-select/dist/types/contrib/microplugin";
import {RecursivePartial, TomOption} from "tom-select/dist/types/types";
import {
    deselectAllButtonTomSelectPlugin,
    selectAllButtonTomSelectPlugin,
    selectedAutoItemsTomSelectPlugin,
    selectedNItemsTomSelectPlugin,
} from "./CustomPlugins";

import {ITomSelectCustomSettings} from "./ITomSelectCustomSettings";

export class TomSelectWrapper {
    private readonly localization: Localization;
    private selectElement: HTMLSelectElement;
    // tslint:disable-next-line:variable-name
    private m_tomSelectInstance: TomSelect | null = null;
    private totalSettings: RecursivePartial<ITomSelectCustomSettings>;

    constructor(
        localization: Localization,
        selectElement: HTMLSelectElement,
        presetSettings?: Partial<ITomSelectCustomSettings>,
    ) {
        this.localization = localization;
        this.selectElement = selectElement;
        this.totalSettings = this.mergeDefaultAndPresetSettings(presetSettings || {});
    }

    public initializeTomSelect(): void {
        if (this.m_tomSelectInstance) {
            this.m_tomSelectInstance.destroy();
        }

        if (this.selectElement === null) {
            return;
        }

        this.enableAdditionalPlugins(this.totalSettings.plugins || {});
        this.m_tomSelectInstance = new TomSelect(this.selectElement, this.totalSettings);

        const values = Array.from(this.selectElement.querySelectorAll("option[selected]")).map((x) => (x as HTMLOptionElement).value);
        if (values.length > 0) {
            this.m_tomSelectInstance.setValue(values);
        }

        if (this.totalSettings.minCount) {
            const originalOnOptionSelectHook = this.m_tomSelectInstance.onOptionSelect;

            this.m_tomSelectInstance.hook("instead", "onOptionSelect", (event: MouseEvent, option: HTMLOptionElement) => {
                const minCount = this.totalSettings.minCount ?? 0;
                if (option.classList.contains("selected") && (this.m_tomSelectInstance?.items.length ?? 0) <= minCount) {
                    return;
                }

                originalOnOptionSelectHook.call(this, event, option);
            });
        }

        if (this.totalSettings.placeholderAtEmptyChoice) {
            this.m_tomSelectInstance.control.dataset.placeholder = this.totalSettings.placeholderAtEmptyChoice;
        }

        this.m_tomSelectInstance.hook("instead", "onItemSelect", (event: MouseEvent, option: HTMLOptionElement) => {
            this.tomSelectInstance?.open();
        });
    }

    public destroy(): void {
        if (this.m_tomSelectInstance) {
            this.m_tomSelectInstance.destroy();
            this.m_tomSelectInstance = null;
        }
    }

    private mergeDefaultAndPresetSettings(presetSettings: Partial<ITomSelectCustomSettings>): RecursivePartial<ITomSelectCustomSettings> {
        let popperInstance: Instance;
        const defaultPlugins = {
            checkbox_options: {},
        };

        let noResultsMessage = "";
        this.localization.translateAsync("no-results-for").then((result) => noResultsMessage = result.result.value);

        const finalSettings: RecursivePartial<ITomSelectCustomSettings> = {
            plugins: {
                ...presetSettings.plugins,
                ...defaultPlugins,
            },
            optionClass: presetSettings.optionClass ? presetSettings.optionClass : "dropdown-item",
            dropdownClass: presetSettings.dropdownClass ? presetSettings.dropdownClass : "dropdown-menu",
            hideSelected: presetSettings.hideSelected ? presetSettings.hideSelected : false as RecursivePartial<boolean>,
            controlInput: presetSettings.controlInput !== undefined ?
                presetSettings.controlInput as RecursivePartial<string | HTMLInputElement> :
                null as unknown as RecursivePartial<string | HTMLInputElement>,
            onInitialize(this: TomSelect): void {
                if (presetSettings.onInitialize) {
                    presetSettings.onInitialize.call(this);
                }

                // Avoid popper warnings about margins
                this.dropdown.style.margin = "unset";
                popperInstance = createPopper(this.control, this.dropdown, {modifiers: [{name: "preventOverflow", enabled: false}]});

            },
            onDropdownOpen(this: TomSelect): void {
                if (presetSettings.onDropdownOpen) {
                    presetSettings.onDropdownOpen.bind(this);
                }

                popperInstance.update();

                // TomSelect does not calculate width when a custom dropdown parent is used
                const width = this.control.getBoundingClientRect().width;
                this.dropdown.style.width = width + "px";
                this.control.scrollIntoView({
                    block: "nearest",
                });
            },
            onDelete: (values: string[], evt: KeyboardEvent | MouseEvent): boolean => {
                const isCountValid = !presetSettings.minCount || (this.m_tomSelectInstance?.items?.length ?? 0) > presetSettings.minCount;
                const shouldCallOnDelete = isCountValid && typeof presetSettings.onDelete === "function";
                return shouldCallOnDelete && presetSettings.onDelete ? presetSettings.onDelete.bind(this)(values, evt) : isCountValid;
            },
            render: {
                option: (presetSettings.render && presetSettings.render.option) ?
                    presetSettings.render.option :
                    (data: TomOption, escape: (str: string) => string) => {
                        return `<div class='d-flex justify-content-between align-items-center py-2 px-4'>
                                    <span> ${escape(data.text)} </span>
                                    <span class='check-mark-dropdown-item ms-2'></span>
                                </div>`;
                    },
                no_results: (presetSettings.render && presetSettings.render.no_results) ?
                    presetSettings.render.no_results :
                    (data: TomOption, escape: (str: string) => string) => {
                        return `<div class='no-results ps-2 py-2 pe-3 '>${noResultsMessage}: ${escape(data.input)}</div>`;
                    },
                optgroup_header: (data: TomOption, escape: (str: string) => string) => {
                    return "<div class=\"optgroup-header dropdown-header\">" + escape(data.label) + "</div>";
                },
            },
        };

        if (presetSettings.render?.item) {
            finalSettings.render = finalSettings.render || {};
            finalSettings.render.item = presetSettings.render.item;
        }
        return {
            ...presetSettings,
            ...finalSettings,
        } as RecursivePartial<ITomSelectCustomSettings>;
    }

    private enableAdditionalPlugins(additionalPlugins: RecursivePartial<string[] | TPluginItem[] | TPluginHash>): void {
        if ("select_all_button" in additionalPlugins) {
            TomSelect.define("select_all_button", selectAllButtonTomSelectPlugin);
        }

        if ("deselect_all_button" in additionalPlugins) {
            TomSelect.define("deselect_all_button", deselectAllButtonTomSelectPlugin);
        }

        if ("item_count" in additionalPlugins) {
            TomSelect.define("item_count", selectedNItemsTomSelectPlugin);
        }

        if ("auto_item" in additionalPlugins) {
            TomSelect.define("auto_item", selectedAutoItemsTomSelectPlugin);
        }
    }

    public get tomSelectInstance(): TomSelect | null {
        return this.m_tomSelectInstance;
    }

    public getTomSelectItems(): string[] {
            return this.m_tomSelectInstance?.items ?? [];
    }

    public markAsSelected(values: string[]): void {
        values.forEach(item => {
            this.m_tomSelectInstance?.addItem(item);
        });
    }

    public unmarkAsSelected(values: string[]): void {
        values.forEach(item => {
            this.m_tomSelectInstance?.removeItem(item);
        });
    }

    public resetInput(): void {
        const items = this.getTomSelectItems();
        while (items.length !== 0) {
            this.m_tomSelectInstance?.removeItem(items[0]);
        }
    }
}
